import { EyeOpenIcon, EyeClosedIcon } from "./EyeIcon";

/**
 * 4つのパスワード画面 (LoginForm / NewPasswordUI / ResetPasswordUI / ChangePasswordPage)
 * で共通利用するパスワード入力フィールド。目アイコンで表示/非表示を切替可能。
 *
 * variant:
 *   - "login":  px-6 py-4 / bg-white     (LoginPage 系の見た目)
 *   - "change": px-4 py-4 / bg-neutral-50 (ChangePasswordPage の見た目)
 */
interface PasswordFieldProps {
  id: string;
  name: string;
  autoComplete: string;
  label?: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  show: boolean;
  onToggle: () => void;
  disabled?: boolean;
  autoFocus?: boolean;
  required?: boolean;
  variant?: "login" | "change";
}

export const PasswordField: React.FC<PasswordFieldProps> = ({
  id,
  name,
  autoComplete,
  label,
  placeholder,
  value,
  onChange,
  show,
  onToggle,
  disabled,
  autoFocus,
  required = true,
  variant = "login",
}) => {
  const inputClass =
    variant === "change"
      ? "w-full px-4 py-4 pr-12 border border-neutral-300 rounded-2xl text-neutral-900 placeholder-neutral-400 focus:outline-none focus:border-primary transition-colors bg-neutral-50"
      : "w-full px-6 py-4 pr-12 border border-neutral-300 rounded-2xl text-neutral-900 placeholder-neutral-400 focus:outline-none focus:border-primary transition-colors bg-white";

  const labelClass =
    variant === "change"
      ? "block text-sm font-semibold text-neutral-700 mb-2"
      : "block text-sm font-medium text-neutral-700 mb-2";

  return (
    <div>
      {label && (
        <label htmlFor={id} className={labelClass}>
          {label}
        </label>
      )}
      <div className="relative">
        <input
          id={id}
          name={name}
          autoComplete={autoComplete}
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          autoFocus={autoFocus}
          required={required}
          placeholder={placeholder ?? "••••••••"}
          className={inputClass}
        />
        <button
          type="button"
          onClick={onToggle}
          disabled={disabled}
          className="absolute right-4 top-1/2 -translate-y-1/2 hover:opacity-80 transition-opacity"
          aria-label={show ? "パスワードを非表示" : "パスワードを表示"}
        >
          {show ? (
            <EyeOpenIcon size={22} className="text-primary" />
          ) : (
            <EyeClosedIcon size={22} className="text-neutral-400" />
          )}
        </button>
      </div>
    </div>
  );
};
